<script lang="js" setup>
import { ref, onMounted, computed } from "vue";
import { fetchGetApi, fetchPostApi } from "@/util/api";
import { useWorkApprovalStore } from '@/store/workApproval';

const allData = ref([]);
import Swal from 'sweetalert2';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-default.css';
import { defineAsyncComponent } from 'vue';

const workApprovalStore = useWorkApprovalStore();

const filterQuery = ref("");
const startDate = ref("");
const endDate = ref("");
const currentPage = ref(1);
const itemsPerPage = ref(10);
const toast = useToast();
const showModal = ref(false);
const selectedCdw = ref(null);
const showImageModal = ref(false);
const selectedImage = ref('');

// Computed property for filtered and paginated data
const filteredData = computed(() => {
    let filtered = [...allData.value];

    // Search filter
    if (filterQuery.value) {
        filtered = filtered.filter(item =>
            item.siteId?.toLowerCase().includes(filterQuery.value.toLowerCase()) ||
            item.serviceId?.toLowerCase().includes(filterQuery.value.toLowerCase()) ||
            item.givenBy?.toLowerCase().includes(filterQuery.value.toLowerCase())
        );
    }

    // Date range filter - now using createdAt instead of workDate
    if (startDate.value && endDate.value) {
        filtered = filtered.filter(item => {
            if (!item.createdAt || item.createdAt === '-') return false;
            const itemDate = new Date(item.createdAt);
            const start = new Date(startDate.value);
            const end = new Date(endDate.value);
            // Set time to start of day for start date and end of day for end date
            start.setHours(0, 0, 0, 0);
            end.setHours(23, 59, 59, 999);
            return itemDate >= start && itemDate <= end;
        });
    }

    return filtered;
});

const paginatedData = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage.value;
    const end = start + itemsPerPage.value;
    return filteredData.value.slice(start, end);
});

const totalPages = computed(() =>
    Math.ceil(filteredData.value.length / itemsPerPage.value)
);

// Action handlers
const handleApprove = async (id) => {
    try {
        await fetchPostApi(`api/cdw/approve/${id}`);
        toast.success('CDW approved successfully');
        // Refresh data and counts
        await loadData();
        await workApprovalStore.fetchPendingCount();
    } catch (error) {
        toast.error('Failed to approve CDW');
    }
};

const handleReject = async (id) => {
    try {
        await fetchPostApi(`api/cdw/reject/${id}`);
        toast.success('CDW rejected successfully');
        // Refresh data and counts
        await loadData();
        await workApprovalStore.fetchPendingCount();
    } catch (error) {
        toast.error('Failed to reject CDW');
    }
};

const handleView = async (id) => {
    try {
        const response = await fetchGetApi(`api/cdw/${id}`);
        if (response && response.data) {
            const item = response.data;
            selectedCdw.value = {
                id: item.id,
                site: typeof item.site === 'string' ? { 
                    name: item.site,
                    address: ''
                } : item.site || { name: '', address: '' },
                submittedBy: typeof item.submittedBy === 'string' ? {
                    name: item.submittedBy,
                    code: ''
                } : item.submittedBy || { name: '', code: '' },
                managers: item.managers?.map(m => ({
                    name: typeof m === 'string' ? m : m.name || '',
                    email: typeof m === 'string' ? '' : m.email || ''
                })) || [],
                alertAuthorities: item.alertAuthorities?.map(a => ({
                    name: typeof a === 'string' ? a : a.name || '',
                    email: typeof a === 'string' ? '' : a.email || ''
                })) || [],
                categories: item.categories?.map(cat => ({
                    ...cat,
                    assigned: cat.assigned?.map(assign => ({
                        ...assign,
                        photo: Array.isArray(assign.photo) ? assign.photo : [assign.photo].filter(Boolean),
                        penalty: assign.penalty || 0
                    })) || []
                })) || [],
                description: item.workDescription || '',
                solution: item.solution || '',
                workDate: item.workDate || '',
                status: (item.status?.toLowerCase() || 'pending'),
                approvedBy: item.approvedBy || '-',
                approvedAt: item.approvedAt || '-',
                createdAt: item.createdAt || '-'
            };
            showModal.value = true;
        }
    } catch (error) {
        toast.error('Failed to fetch CDW details');
    }
};

const closeModal = () => {
    showModal.value = false;
    selectedCdw.value = null;
};

const openImageModal = (imageUrl) => {
    selectedImage.value = imageUrl;
    showImageModal.value = true;
};

const closeImageModal = () => {
    showImageModal.value = false;
    selectedImage.value = '';
};

const loadData = async () => {
    try {
        const response = await fetchGetApi('api/cdw/getAllCdw');
        if (response) {
            const data = Array.isArray(response) ? response :
                (response.data ? response.data : []);

            allData.value = data.map(item => ({
                id: item.id,
                siteId: typeof item.site === 'string' ? item.site : item.site?.name,
                serviceId: item.service,
                givenBy: typeof item.submittedBy === 'string' ? item.submittedBy : item.submittedBy?.name,
                managerIds: item.managers || [],
                alertHigherAuthIds: item.alertAuthorities || [],
                categoryIds: item.categories?.map(cat => cat.category) || [],
                subCategoryIds: item.categories?.flatMap(cat => cat.subCategories) || [],
                workDescription: item.workDescription,
                solution: item.solution,
                workDate: item.workDate,
                status: (item.status?.toLowerCase() || 'pending'),
                approvedBy: item.approvedBy || '-',
                approvedAt: item.approvedAt || '-',
                // Enhanced mapping for modal view
                site: typeof item.site === 'string' ? { 
                    name: item.site,
                    address: ''
                } : item.site || { name: '', address: '' },
                submittedBy: typeof item.submittedBy === 'string' ? {
                    name: item.submittedBy,
                    code: ''
                } : item.submittedBy || { name: '', code: '' },
                service: item.service,
                managers: item.managers?.map(m => ({
                    name: typeof m === 'string' ? m : m.name || '',
                    email: typeof m === 'string' ? '' : m.email || ''
                })) || [],
                alertAuthorities: item.alertAuthorities?.map(a => ({
                    name: typeof a === 'string' ? a : a.name || '',
                    email: typeof a === 'string' ? '' : a.email || ''
                })) || [],
                categories: item.categories?.map(cat => ({
                    ...cat,
                    assigned: cat.assigned?.map(assign => ({
                        ...assign,
                        photo: Array.isArray(assign.photo) ? assign.photo : [assign.photo].filter(Boolean),
                        penalty: assign.penalty || 0
                    })) || []
                })) || [],
                description: item.workDescription,
                assigned: item.categories?.flatMap(cat => cat.assigned || []) || [],
                categoriesWithSubs: item.categories?.map(cat => ({
                    category: cat.category,
                    subCategories: cat.subCategories || []
                })) || [],
                createdAt: item.createdAt || '-',
            }));

            // Debug log
            console.log('Processed data:', allData.value);
        } else {
            toast.error('Failed to fetch data')
        }
    } catch (error) {
        console.error('Error loading data:', error);
        console.log('Response type:', typeof response);
        console.log('Raw response:', response);
        toast.error('Error loading data');
    }
};

onMounted(async () => {
    await loadData();
    await workApprovalStore.fetchPendingCount();
});

</script>

<template>
    <Breadcrumbs main="Apps" title="CDW" />
    <div class="container-fluid">
        <div class="email-wrap bookmark-wrap">
            <div class="row">
                <div class="table-responsive product-table custom-scrollbar">
                    <div class="no-footer">
                        <div class="mb-3 row justify-content-between">
                            <div class="col-md-3">
                                <label class="col-form-label">Search:</label>
                                <input type="text" class="form-control" v-model="filterQuery">
                            </div>
                            <div class="col-md-7">
                                <div class="row">
                                    <div class="col-md-5">
                                        <label class="col-form-label">Start Date:</label>
                                        <input type="date" class="form-control" v-model="startDate">
                                    </div>
                                    <div class="col-md-5">
                                        <label class="col-form-label">End Date:</label>
                                        <input type="date" class="form-control" v-model="endDate">
                                    </div>
                                </div>
                            </div>
                        </div>
                        <table class="display table-striped table-hover table-bordered table" id="basic-1">
                            <thead>
                                <tr>
                                    <th class="sorting_asc" scope="col" style="width: 50px;">SL</th>
                                    <th class="sorting_asc" scope="col">Site ID</th>
                                    <th class="sorting_asc text-nowrap" scope="col">CDW Given Date</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Department</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Given By</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Managers</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Higher Auth</th>
                                    <th class="sorting_asc text-nowrap" scope="col">
                                        <strong>Categories</strong> <br>
                                        <small>Sub Categories</small>
                                    </th>
                                    <th class="sorting_asc text-nowrap" scope="col">Problems</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Work Description</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Solution</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Work Date</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Approved By</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Approved At</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Actions</th>
                                </tr>
                            </thead>
                            <tbody v-if="!paginatedData.length">
                                <tr class="odd">
                                    <td valign="top" colspan="14" class="dataTables_empty">
                                        No matching records found
                                    </td>
                                </tr>
                            </tbody>
                            <tbody v-else>
                                <tr class="odd" v-for="(row, index) in paginatedData" :key="row.id">
                                    <td class="sorting_1" style="width: 10px;">{{ index + 1 }}</td>
                                    <td class="sorting_1">{{ row.siteId }}</td>
                                    <td class="sorting_1 text-nowrap">
                                        {{ row.createdAt ? new Date(row.createdAt).toLocaleString('en-IN', {
                                            timeZone: 'Asia/Kolkata',
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            hour12: true
                                        }) : '-' }}
                                    </td>
                                    <td class="sorting_1">{{ row.serviceId }}</td>
                                    <td class="sorting_1">
                                        {{ row.givenBy }}
                                    </td>
                                    <td class="sorting_1">
                                        <ul>
                                            <li v-for="manager in row.managerIds" :key="manager">{{ manager }}</li>
                                        </ul>
                                    </td>
                                    <td class="sorting_1">
                                        <ul>
                                            <li v-for="auth in row.alertHigherAuthIds" :key="auth">{{ auth }}</li>
                                        </ul>
                                    </td>
                                    <td class="sorting_1">
                                        <div v-for="catGroup in row.categoriesWithSubs" :key="catGroup.category"
                                            class="mb-2">
                                            <strong>{{ catGroup.category }}</strong>
                                            <ul class="list-unstyled ms-3 small">
                                                <li v-for="subCat in catGroup.subCategories" :key="subCat"
                                                    class="text-muted">
                                                    - {{ subCat }}
                                                </li>
                                            </ul>
                                        </div>
                                    </td>
                                    <td class="sorting_1">
                                        <div v-for="catGroup in row.categories" :key="catGroup.category" class="mb-2">
                                            <strong>{{ catGroup.category }}:</strong>
                                            <p class="text-muted mb-0 small">{{ catGroup.problemController || 'N/A' }}</p>
                                            <div v-if="catGroup.imageUrl && catGroup.imageUrl.length" class="mt-2">
                                                <div class="d-flex flex-wrap gap-2">
                                                    <template v-for="(imgUrl, index) in (Array.isArray(catGroup.imageUrl) ? 
                                                        catGroup.imageUrl : 
                                                        catGroup.imageUrl.split(',').filter(url => url.trim()))" 
                                                        :key="index">
                                                        <img 
                                                            v-if="imgUrl.trim()"
                                                            :src="imgUrl.trim()" 
                                                            alt="Category Image" 
                                                            class="img-thumbnail" 
                                                            style="width: 50px; height: 50px; object-fit: cover; cursor: pointer"
                                                            @click="openImageModal(imgUrl.trim())"
                                                        >
                                                    </template>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="sorting_1">{{ row.workDescription }}</td>
                                    <td class="sorting_1">{{ row.solution }}</td>
                                    <td class="sorting_1 text-nowrap">
                                        {{ row.workDate && row.workDate.length > 10 ?
                                            row.workDate.substring(0, 10) : row.workDate }}
                                    </td>
                                    <td class="sorting_1">{{ row.approvedBy }}</td>
                                    <td class="sorting_1 text-nowrap">
                                        {{ row.approvedAt && !isNaN(new Date(row.approvedAt).getTime()) ? 
                                            new Date(row.approvedAt).toLocaleString('en-US', {
                                                year: 'numeric',
                                                month: 'short', 
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            }) : '-' }}
                                    </td>
                                    <td class="text-center">
                                        <button type="button" @click="handleView(row.id)"
                                            class="btn btn-primary btn-sm me-2" title="View">
                                            <i class="fas fa-eye"></i>
                                        </button>
                                        <template v-if="row.status === 'pending'">
                                            <button type="button" @click="handleApprove(row.id)"
                                                class="btn btn-success btn-sm me-2" title="Approve">
                                                <i class="fas fa-check"></i>
                                            </button>
                                            <button type="button" @click="handleReject(row.id)"
                                                class="btn btn-danger btn-sm" title="Reject">
                                                <i class="fas fa-times"></i>
                                            </button>
                                        </template>
                                        <template v-else>
                                            <span
                                                :class="`badge bg-${row.status === 'approved' ? 'success' : 'danger'}`">
                                                {{ row.status.charAt(0).toUpperCase() + row.status.slice(1) }}
                                            </span>
                                        </template>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div class="d-flex justify-content-between align-items-center mt-3">
                            <div class="dataTables_info">
                                Showing {{ ((currentPage - 1) * itemsPerPage) + 1 }} to
                                {{ Math.min(currentPage * itemsPerPage, filteredData.length) }} of
                                {{ filteredData.length }} entries
                            </div>
                            <ul class="pagination">
                                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                                    <a class="page-link" @click="currentPage--"
                                        :disabled="currentPage === 1">Previous</a>
                                </li>
                                <li class="page-item" v-for="page in totalPages" :key="page"
                                    :class="{ active: page === currentPage }">
                                    <a class="page-link" @click="currentPage = page">{{ page }}</a>
                                </li>
                                <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                                    <a class="page-link" @click="currentPage++"
                                        :disabled="currentPage === totalPages">Next</a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="modal fade" :class="{ 'show d-block': showModal }" tabindex="-1" v-if="showModal && selectedCdw">
        <div class="modal-dialog modal-lg">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">CDW Details</h5>
                    <button type="button" class="btn-close" @click="closeModal"></button>
                </div>
                <div class="modal-body">
                    <div class="row g-3">
                        <div class="col-12">
                            <div class="d-flex gap-4 flex-wrap">
                                <div>
                                    <strong class="me-2">Site:</strong>
                                    <span>{{ selectedCdw.site.name }}</span>
                                </div>
                                <div>
                                    <strong class="me-2">Submitted By:</strong>
                                    <span>{{ selectedCdw.submittedBy.name }}</span>
                                </div>
                                <div>
                                    <strong class="me-2">Managers:</strong>
                                    <span>{{ selectedCdw.managers.map(m => m.name).join(', ') }}</span>
                                </div>
                                <div>
                                    <strong class="me-2">Alert Authorities:</strong>
                                    <span>{{ selectedCdw.alertAuthorities.map(a => a.name).join(', ') }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <hr class="my-3">
                    <div class="row mb-3">
                        <div class="col-12">
                            <strong>Categories:</strong>
                            <div v-for="cat in selectedCdw.categories" :key="cat.category" class="mb-3">
                                <p class="mb-1"><strong>{{ cat.category }}:</strong></p>
                                <ul class="list-unstyled ms-3">
                                    <li v-for="subCat in cat.subCategories" :key="subCat">
                                        - {{ subCat }}
                                    </li>
                                </ul>
                                <p class="text-muted mb-2" v-if="cat.problemController">
                                    <strong>Problem:</strong> {{ cat.problemController }}
                                </p>
                                <div v-if="cat.imageUrl && cat.imageUrl.length" class="mt-2">
                                    <p class="mb-1"><strong>Category Images:</strong></p>
                                    <div class="d-flex flex-wrap gap-2">
                                        <template v-for="(imgUrl, index) in (Array.isArray(cat.imageUrl) ? 
                                            cat.imageUrl : 
                                            cat.imageUrl.split(',').filter(url => url.trim()))" 
                                            :key="index">
                                            <img 
                                                v-if="imgUrl.trim()"
                                                :src="imgUrl.trim()" 
                                                alt="Category Image" 
                                                class="img-thumbnail" 
                                                style="width: 100px; height: 100px; object-fit: cover; cursor: pointer"
                                                @click="openImageModal(imgUrl.trim())"
                                            >
                                        </template>
                                    </div>
                                </div>
                                <!-- Assigned Employees per Category -->
                                <div v-if="cat.assigned && cat.assigned.length" class="mt-3">
                                    <p class="mb-1"><strong>Assigned Employees:</strong></p>
                                    <div class="table-responsive">
                                        <table class="table table-bordered table-sm">
                                            <thead class="table-light">
                                                <tr>
                                                    <th>Employee</th>
                                                    <th>Sub Category</th>
                                                    <th>Action</th>
                                                    <th>Photos</th>
                                                    <th>Slip</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="assign in cat.assigned" :key="assign.employee">
                                                    <td>{{ assign.employee }}</td>
                                                    <td>{{ assign.subCategory }}</td>
                                                    <td>
                                                        <span :class="`badge bg-${assign.action === 'penalty' ? 'danger' : 'warning'}`">
                                                            {{ assign.action }}
                                                        </span>
                                                        <span v-if="assign.penalty" class="ms-1">
                                                            (₹{{ assign.penalty }})
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div v-if="assign.photo && assign.photo.length" class="d-flex flex-wrap gap-1">
                                                            <template v-for="(imgUrl, index) in assign.photo" :key="index">
                                                                <img 
                                                                    v-if="imgUrl"
                                                                    :src="imgUrl" 
                                                                    alt="Employee Photo" 
                                                                    class="img-thumbnail" 
                                                                    style="width: 40px; height: 40px; object-fit: cover; cursor: pointer"
                                                                    @click="openImageModal(imgUrl)"
                                                                >
                                                            </template>
                                                        </div>
                                                        <span v-else class="text-muted">No photos</span>
                                                    </td>
                                                    <td>
                                                        <img 
                                                            v-if="assign.slip" 
                                                            :src="assign.slip" 
                                                            alt="Slip" 
                                                            class="img-thumbnail" 
                                                            style="width: 40px; height: 40px; object-fit: cover; cursor: pointer"
                                                            @click="openImageModal(assign.slip)"
                                                        >
                                                        <span v-else class="text-muted">No slip</span>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="row mb-3">
                        <div class="col-12">
                            <strong>Description:</strong>
                            <p class="mb-0">{{ selectedCdw.description }}</p>
                        </div>
                    </div>
                    <div class="row mb-3">
                        <div class="col-12">
                            <strong>Solution:</strong>
                            <p class="mb-0">{{ selectedCdw.solution }}</p>
                        </div>
                    </div>
                    <div class="row mb-3">
                        <div class="col-md-6">
                            <strong>Work Date:</strong>
                            <p class="mb-0">{{ new Date(selectedCdw.workDate).toLocaleDateString('en-IN', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                            }) }}</p>
                        </div>
                        <div class="col-md-6">
                            <strong>Status:</strong>
                            <span :class="`badge bg-${selectedCdw.status === 'approved' ? 'success' :
                                selectedCdw.status === 'rejected' ? 'danger' : 'warning'}`">
                                {{ selectedCdw.status.charAt(0).toUpperCase() + selectedCdw.status.slice(1) }}
                            </span>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-6">
                            <strong>Created At:</strong>
                            <p class="mb-0">{{ selectedCdw.createdAt !== '-' ? 
                                new Date(selectedCdw.createdAt).toLocaleString('en-IN', {
                                    timeZone: 'Asia/Kolkata',
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true
                                }) : '-' }}
                            </p>
                        </div>
                        <div class="col-md-6">
                            <strong>Approved At:</strong>
                            <p class="mb-0">{{ selectedCdw.approvedAt !== '-' ? 
                                new Date(selectedCdw.approvedAt).toLocaleString('en-IN', {
                                    timeZone: 'Asia/Kolkata',
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true
                                }) : '-' }}
                            </p>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="closeModal">Close</button>
                </div>
            </div>
        </div>
    </div>
    <div class="modal-backdrop fade show" v-if="showModal"></div>

    <!-- Image Preview Modal -->
    <div class="modal fade" :class="{ 'show d-block': showImageModal }" tabindex="-1" v-if="showImageModal">
        <div class="modal-dialog modal-lg modal-dialog-centered">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Image Preview</h5>
                    <button type="button" class="btn-close" @click="closeImageModal"></button>
                </div>
                <div class="modal-body text-center">
                    <img :src="selectedImage" alt="Preview" class="img-fluid" style="max-height: 80vh;">
                </div>
            </div>
        </div>
    </div>
    <div class="modal-backdrop fade show" v-if="showImageModal"></div>
</template>

<style scoped>
.modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1040;
}

.modal {
    z-index: 1050;
}

.list-unstyled {
    margin-bottom: 0;
    padding-left: 0;
    list-style: none;
}

.badge {
    text-transform: capitalize;
}

.table {
    margin-bottom: 0;
}

.modal-body {
    max-height: 70vh;
    overflow-y: auto;
}

.sorting_1 ul {
    margin-bottom: 0.5rem;
}

.sorting_1 ul:last-child {
    margin-bottom: 0;
}

.sorting_1 strong {
    color: #333;
    font-size: 0.9rem;
}

.sorting_1 .small {
    font-size: 0.85rem;
}

.text-muted.small {
    font-size: 0.85rem;
    margin-left: 0.5rem;
}
</style>
